import React, { useState } from 'react';
import { 
  X, Download, Trash2, Edit, Link as LinkIcon, 
  Copy, Image, Video, Music, FileText,
  Calendar, User, Folder, Hash, CheckCircle
} from 'lucide-react';
import { useAssetsStore, MediaAsset } from '../../store/assetsStore';
import { useAuthStore } from '../../store/authStore';

interface ViewAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: MediaAsset;
}

const ViewAssetModal: React.FC<ViewAssetModalProps> = ({ isOpen, onClose, asset }) => {
  const { 
    updateAsset, 
    deleteAsset, 
    downloadAsset, 
    copyAssetLink, 
    duplicateAsset 
  } = useAssetsStore();
  const { user } = useAuthStore();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedTags, setEditedTags] = useState<string[]>(asset.tags);
  const [tagInput, setTagInput] = useState('');
  const [editedAltText, setEditedAltText] = useState(asset.altText || '');
  const [editedFolder, setEditedFolder] = useState(asset.folder);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isOpen) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Reset time to midnight for accurate day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const uploadDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today.getTime() - uploadDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Show time if uploaded today
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `Today at ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = () => {
    if (asset.mimeType.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (asset.mimeType.startsWith('video/')) return <Video className="w-5 h-5" />;
    if (asset.mimeType.startsWith('audio/')) return <Music className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !editedTags.includes(trimmedTag)) {
      setEditedTags(prev => [...prev, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEditedTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    await updateAsset(asset.id, {
      tags: editedTags,
      altText: editedAltText,
      folder: editedFolder,
    });
    setIsEditing(false);
  };

  const handleCopyLink = async () => {
    await copyAssetLink(asset.id);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this asset?')) {
      await deleteAsset(asset.id);
      onClose();
    }
  };

  const handleDuplicate = async () => {
    await duplicateAsset(asset.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 truncate">{asset.originalName}</h2>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedTags(asset.tags);
                    setEditedAltText(asset.altText || '');
                    setEditedFolder(asset.folder);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Preview Section */}
            <div className="space-y-4">
              <div className="bg-gray-100 rounded-xl overflow-hidden aspect-video flex items-center justify-center">
                {(() => {
                  const category = asset.mimeType.startsWith('image/') ? 'image' :
                    asset.mimeType.startsWith('video/') ? 'video' :
                    asset.mimeType.startsWith('audio/') ? 'audio' :
                    asset.mimeType.includes('pdf') || asset.mimeType.includes('document') ? 'document' : 'other';
                  const url = asset.thumbnailUrl || asset.cdnUrl;

                  if (category === 'image' && url) {
                    return (
                      <img
                        src={url}
                        alt={asset.altText || asset.originalName}
                        className="w-full h-full object-contain"
                      />
                    );
                  }

                  if (category === 'video' && url) {
                    return (
                      <video
                        src={url}
                        className="w-full h-full object-contain"
                        controls
                        autoPlay={false}
                      />
                    );
                  }

                  if (category === 'audio' && url) {
                    return (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100 p-8">
                        <Music className="w-24 h-24 text-purple-600 mb-4" />
                        <audio
                          src={url}
                          controls
                          className="w-full max-w-md"
                        />
                      </div>
                    );
                  }

                  if (category === 'document' && url && asset.mimeType.includes('pdf')) {
                    return (
                      <iframe
                        src={url}
                        className="w-full h-full"
                        title={asset.originalName}
                      />
                    );
                  }

                  return (
                    <div className="text-gray-400">
                      {getCategoryIcon()}
                    </div>
                  );
                })()}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => downloadAsset(asset.id)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  {linkCopied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4" />
                      Copy Link
                    </>
                  )}
                </button>
                <button
                  onClick={handleDuplicate}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              {/* File Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">File Information</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-24">Filename:</span>
                    <span className="text-sm text-gray-900 flex-1 truncate">{asset.originalName}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-24">Size:</span>
                    <span className="text-sm text-gray-900">{formatFileSize(asset.fileSize)}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-24">Type:</span>
                    <span className="text-sm text-gray-900 uppercase">{asset.mimeType.split('/')[1]?.split(';')[0] || 'file'}</span>
                  </div>
                  
                  {asset.mimeType.startsWith('image/') && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600 w-24">Dimensions:</span>
                      <span className="text-sm text-gray-900">-</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Metadata</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Uploaded:</span>
                    <span className="text-sm text-gray-900">{formatDate(asset.createdAt)}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Uploaded by:</span>
                    <span className="text-sm text-gray-900">
                      {user && asset.uploadedBy === user.id 
                        ? `${user.firstName} ${user.lastName}` 
                        : asset.uploadedBy || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Folder className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Folder:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedFolder}
                        onChange={(e) => setEditedFolder(e.target.value)}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{asset.folder}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
                  {isEditing && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        placeholder="Add tag"
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        onClick={addTag}
                        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  )}
                </div>
                
                {editedTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {editedTags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                      >
                        <Hash className="w-3 h-3" />
                        {tag}
                        {isEditing && (
                          <button
                            onClick={() => removeTag(tag)}
                            className="hover:text-primary-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No tags added</p>
                )}
              </div>

              {/* Alt Text */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Alt Text</h3>
                {isEditing ? (
                  <textarea
                    value={editedAltText}
                    onChange={(e) => setEditedAltText(e.target.value)}
                    placeholder="Describe the image for screen readers"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                ) : (
                  <p className="text-sm text-gray-600">{asset.altText || 'No alt text provided'}</p>
                )}
              </div>

              {/* Usage Stats */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Usage</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Used in projects:</span>
                    <span className="text-sm font-medium text-gray-900">{asset.usageCount}</span>
                  </div>
                  {asset.usedIn.length > 0 && (
                    <div className="text-sm text-gray-500">
                      {asset.usedIn.length} content item{asset.usedIn.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAssetModal;

